import { DialogContent, DialogTitle, DialogFooter, DialogHeader } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertCircle, Info, X, ChevronDown } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { MaterialEntry, Option } from "@/types/material";
import { useMaterialForm } from "../../hooks/useMaterialForm";

interface MaterialModalFormProps {
  onClose: () => void;
  onSave: () => void;
  formEntry: MaterialEntry;
  onFormEntryChange: (entry: MaterialEntry) => void;
  materialOptions: Option[];
  unitOptions: string[];
  editing: boolean;
}

export const MaterialModalForm = ({
  onClose,
  onSave,
  formEntry,
  onFormEntryChange,
  materialOptions,
  unitOptions,
  editing,
}: MaterialModalFormProps) => {
  const { t } = useLanguage();
  const {
    dropdownRef,
    search,
    showDropdown,
    materialTypeError,
    quantityError,
    touched,
    filteredOptions,
    isFormValid,
    setIsScrolling,
    handleTypeChange,
    handleTypeSelect,
    handleInputClick,
    handleTypeBlur,
    handleUnitChange,
    handleQuantityChange,
    handleQuantityBlur,
    handleSaveClick,
  } = useMaterialForm({
    formEntry,
    materialOptions,
    editing,
    onFormEntryChange,
    onSave,
  });

  return (
    <DialogContent className="sm:max-w-[600px]">
      <DialogHeader>
        <DialogTitle>
          {editing ? t("common.edit") : t("common.add")} {t("materials.name")}
        </DialogTitle>
      </DialogHeader>

      <div className="grid gap-4 py-4">
        {/* Material Type */}
        <div className="grid gap-2">
          <Label htmlFor="materialType" className="font-medium">
            {t("materials.materialType")} *
          </Label>
          <div className="relative" ref={dropdownRef}>
            <div className="relative">
              <input
                id="materialType"
                type="text"
                value={search}
                onChange={handleTypeChange}
                onBlur={handleTypeBlur}
                onClick={handleInputClick}
                placeholder={t("materials.selectMaterialType")}
                autoComplete="off"
                spellCheck="false"
                className={`w-full h-11 px-3 py-2 cursor-pointer pr-10 border rounded-md outline-none focus:outline-none ${
                  materialTypeError && touched.materialType ? "border-red-500" : "border-gray-200"
                }`}
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                {search && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTypeSelect({ value: "", label: "" }, e);
                    }}
                    className="text-gray-400 hover:text-gray-600 p-1 rounded-full mr-1"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
                <ChevronDown
                  className={`h-4 w-4 text-gray-500 transition-transform ${showDropdown ? "transform rotate-180" : ""}`}
                />
              </div>
            </div>
            {showDropdown && filteredOptions.length > 0 && (
              <div
                className="absolute z-50 w-full mt-1 border bg-white rounded-md shadow-lg overflow-hidden"
                style={{ maxHeight: "320px", overflowY: "auto" }}
                onMouseDown={(e) => {
                  // Prevent dropdown from closing when clicking on scrollbar
                  const target = e.currentTarget;
                  const rect = target.getBoundingClientRect();
                  const isScrollbarClick = e.clientX > rect.right - 16;
                  
                  if (isScrollbarClick) {
                    e.stopPropagation();
                    e.preventDefault();
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onPointerDown={(e) => {
                  e.stopPropagation();
                }}
                onScroll={() => {
                  // Set scrolling state when user scrolls
                  setIsScrolling(true);
                  // Reset after scrolling stops
                  setTimeout(() => setIsScrolling(false), 150);
                }}
              >
                {filteredOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className="w-full text-left px-4 py-3.5 hover:bg-teal-50 cursor-pointer transition-colors border-b border-gray-100 last:border-0"
                    onMouseDown={(e) => handleTypeSelect(option, e)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {materialTypeError && touched.materialType && (
            <div className="flex items-center text-xs text-red-500 mt-1">
              <AlertCircle className="h-3 w-3 mr-1" />
              {materialTypeError}
            </div>
          )}
        </div>

        {/* Quantity and Unit */}
        <div className="grid gap-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="quantity" className="font-medium">
              {t("common.quantity")} *
            </Label>
          </div>

          {!formEntry.materialType ? (
            <div className="space-y-4">
              <Input
                id="quantity"
                type="number"
                min="0"
                step="0.01"
                value={formEntry.quantity === "" ? "" : formEntry.quantity}
                onChange={(e) => handleQuantityChange(e.target.value)}
                onBlur={handleQuantityBlur}
                placeholder="0.00"
                autoComplete="off"
                disabled={!formEntry.materialType}
                className={`py-3 rounded-md text-lg ${
                  quantityError
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-200 focus:ring-teal-500 focus:border-teal-500"
                }`}
              />

              <div className="bg-gray-50 border border-gray-200 rounded-md p-4 flex items-center gap-3 text-gray-600">
                <Info className="h-5 w-5 text-teal-600 flex-shrink-0" />
                <p className="text-sm">{t("materials.selectMaterialType")}</p>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <div className="relative flex-grow basis-2/3">
                <Input
                  id="quantity"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formEntry.quantity === "" ? "" : formEntry.quantity}
                  onChange={(e) => handleQuantityChange(e.target.value)}
                  onBlur={handleQuantityBlur}
                  placeholder="0.00"
                  autoComplete="off"
                  disabled={!formEntry.materialType}
                  className={`py-3 rounded-md text-lg ${
                    quantityError
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-200 focus:ring-teal-500 focus:border-teal-500"
                  }`}
                />
              </div>

              <div className="flex-grow basis-1/3">
                {unitOptions.length === 0 ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-md h-10 flex items-center gap-2 px-3 text-gray-600">
                    <Info className="h-4 w-4 text-amber-600 flex-shrink-0" />
                    <p className="text-xs">{t("materials.noUnitsAvailable") || "No units available"}</p>
                  </div>
                ) : unitOptions.length === 1 ? (
                  <div className="h-10 flex items-center px-3 py-2 border border-input bg-background rounded-md">
                    {unitOptions[0]}
                  </div>
                ) : (
                  <Select value={formEntry.unit} onValueChange={handleUnitChange}>
                    <SelectTrigger id="unit" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {unitOptions.map((unit) => (
                        <SelectItem key={unit} value={unit}>
                          {unit}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          )}

          {quantityError && touched.quantity && (
            <div className="flex items-center text-xs text-red-500 mt-1">
              <AlertCircle className="h-3 w-3 mr-1" />
              {t("validation.positiveNumber")}
            </div>
          )}
        </div>

        {/* Footprint Info Card */}
        {formEntry.materialType && (
          <div className="bg-cyan-50 border border-cyan-100 rounded-md p-4 mt-2">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-cyan-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-700">{t("materials.footprintInfo")}</p>
            </div>
          </div>
        )}
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button
          type="button"
          onClick={() => handleSaveClick(t)}
          className="bg-teal-600 text-white hover:bg-teal-700"
          disabled={!isFormValid()}
        >
          {t("common.save")}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};
